import {
  Text,
  Button,
  PrimaryButton,
  Heading,
} from '@janeapp/burrito-design-system';
import { Button as AriaButton } from 'react-aria-components';
import { BadgeList } from '..';
import { PractitionersSearchResult } from '~/api';
import { PractitionerPhoto } from '../PractitionerPhoto';
import { NavigationMenu } from '../SearchHeader/components/NavigationMenu';
import {
  appendDiscoveryTokenToBookingUrl,
  buildPractitionerBookingUrl,
  formatAddress,
  formatDate,
} from '~/utilities';
import { ChevronLeft } from '@janeapp/burrito-design-system/icons';
import { clearSelectedPractitioner } from '~/state/activeSelection';
import { useAppDispatch, useAppSelector } from '~/state';
import JaneLogoImg from '~/assets/images/jane_logo_200.svg';
import { CollapsibleContent, Openings } from './components';
import { PractitionersAtLocation } from '../PractitionersAtLocation';
import { useEffect, useMemo } from 'react';
import { selectDiscipline } from '~/state/discipline';
import { trackEvent, useView } from '~/hooks';
import { trackAvailabilityStaleness } from '~/hooks';
import { OpeningBadge } from '../OpeningBadge';
import { StaticMap } from './components/StaticMap';

interface PractitionerLocationDetailsProps {
  result: PractitionersSearchResult;
}

export const PractitionerLocationDetails = ({
  result,
}: PractitionerLocationDetailsProps) => {
  const { isMobile } = useView();
  const dispatch = useAppDispatch();
  const discipline = useAppSelector(selectDiscipline);
  const openingsBadgeStatus = result.firstOpening
    ? formatDate(result.firstOpening?.startAt)
    : undefined;

  useEffect(() => {
    trackEvent('mpl_practitioner_details_viewed', {
      name: result.fullName,
      clinic_name: result.clinicName,
    });
    trackAvailabilityStaleness(result, openingsBadgeStatus);
  }, [openingsBadgeStatus, result]);

  const handleBookNowButtonClick = () => {
    const bookingUrl = buildPractitionerBookingUrl(
      result.staffMemberGuid,
      result.clinicBookingUrl
    );
    trackEvent('mpl_book_now_button_clicked', {
      name: result.fullName,
      clinic_name: result.clinicName,
      url: bookingUrl,
      discipline,
    });
    window.open(bookingUrl, '_blank');
  };

  const handleViewBookingSite = () => {
    if (!result.clinicBookingUrl) return;
    trackEvent('mpl_view_booking_site_button_clicked', {
      name: result.fullName,
      clinic_name: result.clinicName,
      url: result.clinicBookingUrl,
    });
    window.open(
      appendDiscoveryTokenToBookingUrl(result.clinicBookingUrl),
      '_blank'
    );
  };

  const sortedPractitionerDisciplines = useMemo(() => {
    if (!discipline || !result.practitionerDisciplines?.length) {
      return result.practitionerDisciplines;
    }

    return [...result.practitionerDisciplines].sort((a, b) => {
      if (a.toLowerCase() === discipline.toLowerCase()) return -1;
      if (b.toLowerCase() === discipline.toLowerCase()) return 1;
      return 0;
    });
  }, [discipline, result.practitionerDisciplines]);

  return (
    <div
      data-testid="practitioner-location-details"
      className="
        grid-area-details grid grid-rows-[auto_1fr] overflow-hidden border-r border-neutral-20
        max-md:fixed max-md:inset-0 max-md:z-50 max-md:border-r-0 max-md:bg-white
        max-md:pb-[env(safe-area-inset-bottom)] max-md:pt-[env(safe-area-inset-top)]
      "
    >
      <div className="flex flex-row items-center justify-between border-b border-neutral-20 bg-neutral-10 p-4">
        <AriaButton
          data-testid="back-button"
          aria-label="Back to search results"
          onPress={() => dispatch(clearSelectedPractitioner())}
          className="focus-ring rounded p-[10px] text-neutral-80 hover:bg-brand-10 hover:text-brand-90"
        >
          <ChevronLeft />
        </AriaButton>
        <img
          src={JaneLogoImg}
          className="h-auto w-[50px]"
          alt="Jane Logo"
          width="50"
          height="41"
        />
        <NavigationMenu />
      </div>
      <div className="overflow-y-auto">
        <div className="flex flex-col border-b border-neutral-30 p-5 pt-7">
          <div className="mb-5 flex flex-row gap-4">
            <PractitionerPhoto
              photo={result.photo}
              size="md"
              name={result.fullName}
            />
            <div className="flex flex-col justify-center gap-1">
              <Heading level={1} className="m-0 text-xl font-semibold">
                {result.fullName}
              </Heading>
              <div className="flex flex-row flex-wrap items-center gap-1">
                <Text className="text-md">{result.clinicName}</Text>
                {result.pronouns && (
                  <Text
                    elementType="span"
                    className="whitespace-nowrap"
                    color="subdued"
                    data-testid="practitioner-pronouns"
                  >
                    · {result.pronouns}
                  </Text>
                )}
              </div>
              <BadgeList
                dataTestId="practitioner-disciplines-badge"
                id="practitioner-detail-list-disciplines"
                badges={sortedPractitionerDisciplines}
                maxToShow={2}
                className="mt-1"
              />
              <OpeningBadge badgeStatus={openingsBadgeStatus} />
            </div>
          </div>
          <PrimaryButton
            className="w-100 branded-button mb-4 text-md font-normal"
            data-testid="practitioner-book-button"
            onClick={handleBookNowButtonClick}
          >
            Book with {result.firstName}
          </PrimaryButton>
          {result.description && (
            <CollapsibleContent content={result.description} />
          )}
          <Openings result={result} />
        </div>
        <div
          className="mx-4 flex flex-col gap-4 border-neutral-30 pb-3 pt-7"
          data-testid="clinic-details"
        >
          <Heading level={3} className="font-semibold">
            Clinic information
          </Heading>
          <div className="flex flex-col rounded-md border border-neutral-30">
            {isMobile ? (
              <StaticMap
                clinicLocationGuid={result.clinicLocationGuid}
                clinicName={result.clinicName}
                address={result.streetAddress}
              />
            ) : (
              result.clinicLogo && (
                <div className="flex h-[180px] w-auto items-center justify-center border-b border-neutral-30">
                  {/* TODO: Add placeholder img for clinic logo when none is available */}
                  <img
                    data-testid="clinic-logo"
                    src={result.clinicLogo}
                    className="h-full w-auto object-contain"
                    alt={`${result.clinicName}'s logo`}
                    width="180"
                    height="180"
                  />
                </div>
              )
            )}
            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-1 p-4">
                <Text className="text-lg font-semibold">
                  {result.clinicName}
                </Text>
                <Text className="text-md">
                  {formatAddress(
                    result.streetAddress2,
                    result.streetAddress,
                    result.city,
                    result.province
                  )}
                </Text>
                <Text className="mb-2 text-md">{result.clinicPhone}</Text>
                <BadgeList
                  dataTestId="clinic-disciplines-badge"
                  badges={result.disciplines}
                  id="clinic-disciplines"
                />
                <Button
                  className="w-100 mt-4 text-md"
                  onClick={handleViewBookingSite}
                >
                  View booking site
                </Button>
              </div>
            </div>
          </div>
        </div>
        <PractitionersAtLocation
          marketplacePractitionerLocationId={
            result.marketplacePractitionerLocationId
          }
          clinicLocationGuid={result.clinicLocationGuid}
          clinicName={result.clinicName}
          clinicBookingUrl={result.clinicBookingUrl}
        />
      </div>
    </div>
  );
};
